<script>
    import { onMount, onDestroy } from 'svelte';
    import { fade } from 'svelte/transition';
    import { initGoogleSlidesAPI, exportToGoogleSlides, ensureGoogleAuthentication, hasSlidesPermissions } from '../services/slidesService.js';
    import { createGoogleSlides } from '../services/slidesService';
    import { get } from 'svelte/store';
    import { storyStore } from '../stores/storyStore.js';
    import { authStore } from '../stores/authStore.js';
    import { generatePreviewForSlide } from '../utils/previewGenerator.js';
    
    export let isOpen = false;
    export let onClose = () => {};
    export let data = null;
    
    let isLoading = false;
    let isExporting = false;
    let error = null;
    let exportResult = null;
    let slidesHistory = [];
    let showHistory = false;
    let renderedSlides = [];
    let isGeneratingPreviews = false;
    
    // State flags for the UI
    let showPermissionStep = false;
    let showExportStep = false;
    let showSuccessStep = false;
    
    onMount(async () => {
        try {
            // Initialize Google API
            await initGoogleSlidesAPI();
            // Load slides history
            await loadSlidesHistory();
            // Generate previews
            await generatePreviews();
        } catch (err) {
            console.error('Error initializing:', err);
            error = err;
        } finally {
            // Always show the permission step so users can retry authentication
            showPermissionStep = true;
        }

        // Listen for authentication success message
        window.addEventListener('message', (event) => {
            if (event.data === 'authentication-successful') {
                handleAuthSuccess();
            }
        });
    });
    
    async function loadSlidesHistory() {
        try {
            const currentStory = get(storyStore);
            if (!currentStory?._id) return;
            
            const response = await fetch(`/api/slides/story/${currentStory._id}`);
            if (!response.ok) throw new Error('Failed to load slides history');
            
            slidesHistory = await response.json();
        } catch (err) {
            console.error('Error loading slides history:', err);
        }
    }
    
    async function saveSlidesExport(presentationUrl, presentationId) {
        try {
            const currentStory = get(storyStore);
            if (!currentStory?._id) return;
            
            // Extract presentation ID from URL if not provided
            if (!presentationId && presentationUrl) {
                const match = presentationUrl.match(/\/d\/([^/]+)/);
                if (match) {
                    presentationId = match[1];
                }
            }
            
            // Ensure we have both required fields
            if (!presentationUrl || !presentationId) {
                throw new Error('Missing presentation URL or ID');
            }
            
            const response = await fetch('/api/slides', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    storyId: currentStory._id,
                    url: presentationUrl,
                    presentationId: presentationId,
                    title: currentStory.title || 'Untitled Slides',
                    description: `Slides generated from ${currentStory.title || 'story'}`
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save slides export');
            }
            
            // Refresh history after saving
            await loadSlidesHistory();
        } catch (err) {
            console.error('Error saving slides export:', err);
            throw err;
        }
    }
    
    function handleAuthSuccess() {
        showPermissionStep = false;
        createSlides();
    }
    
    async function createSlides() {
        if (!data) {
            error = new Error('No data provided for slides creation');
            return;
        }

        isLoading = true;
        error = null;

        try {
            const result = await createGoogleSlides(data);
            if (result.success) {
                await saveSlidesExport(result.viewUrl, result.presentationId);
            }
            onClose();
        } catch (err) {
            console.error('Error creating slides:', err);
            error = err;
        } finally {
            isLoading = false;
        }
    }
    
    // Function to check permissions and handle export
    async function handleExport() {
        showPermissionStep = false;
        showExportStep = true;
        isExporting = true;
        error = null;

        try {
            // First check if we have Slides permissions
            console.log('Checking Slides permissions...');
            const hasPermissions = await hasSlidesPermissions();
            
            if (!hasPermissions) {
                console.log('Need additional permissions for Slides...');
                // Get current user's email
                const userEmail = get(authStore).user?.email;
                
                // Request additional permissions
                const isAuthenticated = await ensureGoogleAuthentication();
                if (!isAuthenticated) {
                    throw new Error('Failed to get Slides permissions. Please try again.');
                }
            }

            console.log('Starting export...');
            exportResult = await exportToGoogleSlides();

            if (exportResult.success) {
                console.log('Export successful:', exportResult.presentationUrl);
                await saveSlidesExport(
                    exportResult.presentationUrl,
                    exportResult.presentationId
                );
                showExportStep = false;
                showSuccessStep = true;
            } else {
                console.error('Export failed:', exportResult.error);
                error = exportResult.error || 'Export failed for an unknown reason.';
            }
        } catch (err) {
            console.error('Error during export:', err);
            error = err.message || 'Export failed. Please try again.';
        } finally {
            isExporting = false;
        }
    }
    
    // Function to open the exported slides in a new tab
    function openExportedSlides() {
        if (exportResult?.presentationUrl) {
            window.open(exportResult.presentationUrl, '_blank');
        }
    }
    
    // Function to open a slides presentation from history
    function openSlidesFromHistory(slides) {
        window.open(slides.url, '_blank');
    }
    
    // Reset the modal state when it's closed
    function handleClose() {
        isLoading = false;
        isExporting = false;
        error = null;
        exportResult = null;
        showPermissionStep = true;
        showExportStep = false;
        showSuccessStep = false;
        showHistory = false;
        onClose();
    }
    
    // Toggle history view
    function toggleHistory() {
        showHistory = !showHistory;
    }

    async function generatePreviews() {
        if (!data?.slides?.length) return;
        
        isGeneratingPreviews = true;
        try {
            // Render each slide with progress tracking
            renderedSlides = await Promise.all(data.slides.map(async (slide, index) => {
                if (slide) {
                    const dataUrl = await renderCanvas(slide);
                    return { ...slide, dataUrl };
                }
                return slide;
            }));
        } catch (err) {
            console.error('Error generating previews:', err);
            error = err;
        } finally {
            isGeneratingPreviews = false;
        }
    }

    async function renderCanvas(slide) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = 375;
        tempCanvas.height = 667;
        const fabricCanvas = new window.fabric.Canvas(tempCanvas);

        if (slide.canvasState && slide.canvasState.length > 0) {
            try {
                // Add a white background
                const backgroundRect = new window.fabric.Rect({
                    left: 0,
                    top: 0,
                    width: fabricCanvas.width,
                    height: fabricCanvas.height,
                    fill: 'white',
                    selectable: false,
                    evented: false,
                });
                fabricCanvas.add(backgroundRect);
                fabricCanvas.sendToBack(backgroundRect);

                // Load the canvas state
                await new Promise((resolve) => {
                    fabricCanvas.loadFromJSON(
                        { version: '5.2.4', objects: slide.canvasState },
                        () => {
                            fabricCanvas.renderAll();
                            resolve();
                        }
                    );
                });

                return tempCanvas.toDataURL('image/png');
            } catch (error) {
                console.error('Error rendering canvas:', error);
                // Return a blank white canvas on error
                const ctx = tempCanvas.getContext('2d');
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
                return tempCanvas.toDataURL('image/png');
            }
        } else {
            // If no canvas state, create a blank white canvas
            const ctx = tempCanvas.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            return tempCanvas.toDataURL('image/png');
        }
    }
</script>

{#if isOpen}
    <div class="modal-backdrop" on:click={handleClose}>
        <div class="modal-content" on:click|stopPropagation>
            <div class="modal-header">
                <h2>Preview Google Slides Export</h2>
                <button class="close-btn" on:click={handleClose}>×</button>
            </div>
            
            <div class="tabs">
                <button 
                    class="tab-button {!showHistory ? 'active' : ''}" 
                    on:click={() => showHistory = false}
                >
                    Preview
                </button>
                <button 
                    class="tab-button {showHistory ? 'active' : ''}" 
                    on:click={toggleHistory}
                >
                    History ({slidesHistory.length})
                </button>
            </div>
            
            {#if showHistory}
                <div class="modal-body">
                    <div class="history-step">
                        <h3>Previous Exports</h3>
                        {#if slidesHistory.length > 0}
                            <div class="slides-list">
                                {#each slidesHistory as slides}
                                    <div class="slides-item" on:click={() => openSlidesFromHistory(slides)}>
                                        <div class="slides-info">
                                            <h4>{slides.title}</h4>
                                            <p>{new Date(slides.createdAt).toLocaleDateString()}</p>
                                        </div>
                                        <button class="open-btn">
                                            <i class="bi bi-box-arrow-up-right"></i>
                                        </button>
                                    </div>
                                {/each}
                            </div>
                        {:else}
                            <p class="no-history">No previous exports found</p>
                        {/if}
                    </div>
                </div>
            {:else}
                <div class="modal-body">
                    <div class="preview-container">
                        <div class="slides-preview">
                            {#if isGeneratingPreviews}
                                <div class="loading-overlay">
                                    <div class="loading-spinner"></div>
                                    <p>Generating slide previews...</p>
                                </div>
                            {:else if renderedSlides.length > 0}
                                <div class="slides-grid">
                                    {#each renderedSlides as slide, index}
                                        <div class="slide-preview">
                                            <div class="slide-number">Slide {index + 1}</div>
                                            {#if slide.dataUrl}
                                                <img src={slide.dataUrl} alt="Slide preview" />
                                            {/if}
                                            {#if slide.script}
                                                <div class="slide-script">
                                                    <small>{slide.script}</small>
                                                </div>
                                            {/if}
                                        </div>
                                    {/each}
                                </div>
                            {:else}
                                <p class="no-slides">No slides to preview</p>
                            {/if}
                        </div>
                        
                        <div class="export-actions">
                            {#if error}
                                <div class="error-message">
                                    <i class="bi bi-exclamation-triangle"></i>
                                    <span>{error.message || error}</span>
                                </div>
                            {/if}
                            
                            {#if isExporting}
                                <div class="export-progress">
                                    <div class="loading-spinner"></div>
                                    <p>Creating your Google Slides presentation...</p>
                                </div>
                            {:else if showSuccessStep}
                                <div class="success-message">
                                    <i class="bi bi-check-circle success-icon"></i>
                                    <p>Export successful! Your presentation is ready.</p>
                                    <button class="primary-btn" on:click={openExportedSlides}>
                                        Open in Google Slides
                                    </button>
                                </div>
                            {:else}
                                <button 
                                    class="export-btn" 
                                    on:click={handleExport}
                                    disabled={!renderedSlides.length}
                                >
                                    <i class="bi bi-google"></i>
                                    Export to Google Slides
                                </button>
                            {/if}
                        </div>
                    </div>
                </div>
            {/if}
        </div>
    </div>
{/if}

<style>
    .modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
    }
    
    .modal-content {
        background: white;
        border-radius: 8px;
        width: 90%;
        max-width: 500px;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        position: relative;
        z-index: 10000;
    }
    
    .modal-header {
        padding: 1rem;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    
    .modal-header h2 {
        margin: 0;
        font-size: 1.25rem;
        color: #111827;
    }
    
    .close-btn {
        background: none;
        border: none;
        font-size: 1.5rem;
        color: #6b7280;
        cursor: pointer;
        padding: 0.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: all 0.2s;
    }
    
    .close-btn:hover {
        background: #f3f4f6;
        color: #111827;
    }
    
    .modal-body {
        padding: 2rem;
    }
    
    .permission-step, .export-step, .success-step {
        text-align: center;
    }
    
    .permission-step h3, .success-step h3 {
        margin: 0 0 1rem 0;
        font-size: 1.5rem;
        color: #111827;
    }
    
    .permission-step p {
        color: #6b7280;
        margin-bottom: 2rem;
    }
    
    /* Google Sign-In button styles */
    .google-signin-container {
        display: flex;
        justify-content: center;
        margin: 1.5rem 0;
    }
    
    .google-signin-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        background: white;
        border: 1px solid #dadce0;
        border-radius: 4px;
        padding: 8px 16px;
        font-family: 'Google Sans', Roboto, Arial, sans-serif;
        font-size: 14px;
        font-weight: 500;
        color: #3c4043;
        cursor: pointer;
        transition: all 0.2s;
        height: 40px;
        min-width: 220px;
    }
    
    .google-signin-button:hover {
        background: #f8f9fa;
        border-color: #dadce0;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
    
    .google-signin-button img {
        width: 18px;
        height: 18px;
    }
    
    .error-message {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #dc2626;
        background: #fee2e2;
        padding: 0.75rem;
        border-radius: 4px;
        margin-top: 1rem;
        font-size: 0.875rem;
    }
    
    .loading-spinner {
        border: 3px solid #f3f3f3;
        border-radius: 50%;
        border-top: 3px solid #3b82f6;
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
        margin: 0 auto 1rem auto;
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    
    .success-icon {
        font-size: 3rem;
        color: #10b981;
        margin-bottom: 1rem;
    }
    
    .primary-btn {
        background: #2563eb;
        color: white;
        border: none;
        padding: 0.75rem 1.5rem;
        border-radius: 4px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s;
    }
    
    .primary-btn:hover {
        background: #1d4ed8;
    }
    
    .export-steps {
        margin: 20px 0;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
    
    .step {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    
    .step-icon {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    
    .step.active .step-icon i {
        animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
    
    .error-help {
        margin-top: 10px;
        padding: 10px;
        background-color: rgba(255, 0, 0, 0.05);
        border-radius: 4px;
    }
    
    .error-help p {
        font-weight: bold;
        margin-bottom: 5px;
    }
    
    .error-help ul {
        margin-left: 20px;
    }
    
    .retry-btn {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        margin-top: 10px;
        padding: 8px 12px;
        background-color: #f1f1f1;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
    }
    
    .retry-btn:hover {
        background-color: #e1e1e1;
    }
    
    .retry-btn i {
        font-size: 16px;
    }
    
    .history-step {
        text-align: center;
    }
    
    .history-step h3 {
        margin: 0 0 1rem 0;
        font-size: 1.5rem;
        color: #111827;
    }
    
    .slides-list {
        margin: 1rem 0;
        max-height: 300px;
        overflow-y: auto;
    }
    
    .slides-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        margin-bottom: 0.5rem;
        cursor: pointer;
        transition: all 0.2s;
    }
    
    .slides-item:hover {
        background: #f3f4f6;
    }
    
    .slides-info h4 {
        margin: 0;
        font-size: 1rem;
        color: #111827;
    }
    
    .slides-info p {
        margin: 0.25rem 0 0 0;
        font-size: 0.875rem;
        color: #6b7280;
    }
    
    .open-btn {
        background: none;
        border: none;
        color: #3b82f6;
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 4px;
        transition: all 0.2s;
    }
    
    .open-btn:hover {
        background: #f3f4f6;
    }
    
    .no-history {
        color: #6b7280;
        margin: 2rem 0;
    }
    
    .back-btn, .history-btn {
        background: #f3f4f6;
        color: #374151;
        border: none;
        padding: 0.75rem 1.5rem;
        border-radius: 4px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        margin-top: 1rem;
    }
    
    .back-btn:hover, .history-btn:hover {
        background: #e5e7eb;
    }
    
    /* Tabs styling */
    .tabs {
        display: flex;
        padding: 0 1rem;
        border-bottom: 1px solid #ddd;
        background: white;
    }
    
    .tab-button {
        padding: 1rem 1.5rem;
        border: none;
        background: none;
        cursor: pointer;
        font-size: 1rem;
        border-bottom: 3px solid transparent;
        transition: border-color 0.2s ease;
        color: #666;
    }
    
    .tab-button.active {
        border-bottom: 3px solid #007bff;
        font-weight: 600;
        color: #007bff;
    }
    
    .tab-button:hover {
        color: #007bff;
    }
    
    /* History styling */
    .history-step {
        padding: 20px;
    }
    
    .slides-list {
        display: flex;
        flex-direction: column;
        gap: 15px;
        margin-top: 20px;
    }
    
    .slides-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px;
        border: 1px solid #eee;
        border-radius: 8px;
        background-color: #f9f9f9;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .slides-item:hover {
        background-color: #f0f0f0;
    }
    
    .slides-info {
        flex: 1;
    }
    
    .slides-info h4 {
        margin: 0 0 8px 0;
        font-size: 1.1rem;
    }
    
    .slides-info p {
        color: #666;
        font-size: 0.9rem;
        margin: 0;
    }
    
    .open-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 8px;
        border-radius: 4px;
        transition: all 0.2s ease;
    }
    
    .open-btn:hover {
        background-color: #e9ecef;
    }
    
    .no-history {
        text-align: center;
        color: #666;
        padding: 40px 0;
        font-size: 1.1rem;
    }
    
    .back-btn {
        display: none; /* Hide the back button since we now have tabs */
    }
    
    .preview-container {
        display: flex;
        flex-direction: column;
        gap: 20px;
        height: 100%;
    }
    
    .slides-preview {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 8px;
    }
    
    .slides-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 20px;
    }
    
    .slide-preview {
        background: white;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        padding: 10px;
        position: relative;
    }
    
    .slide-preview img {
        width: 100%;
        height: auto;
        border-radius: 2px;
    }
    
    .slide-number {
        position: absolute;
        top: -10px;
        left: 50%;
        transform: translateX(-50%);
        background: #6c757d;
        color: white;
        padding: 2px 8px;
        border-radius: 12px;
        font-size: 12px;
    }
    
    .slide-script {
        margin-top: 10px;
        padding: 8px;
        background: #f8f9fa;
        border-radius: 4px;
        font-size: 12px;
        max-height: 60px;
        overflow-y: auto;
    }
    
    .export-actions {
        padding: 20px;
        border-top: 1px solid #dee2e6;
        text-align: center;
    }
    
    .export-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        background: #4285f4;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 4px;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        width: 100%;
        max-width: 300px;
        margin: 0 auto;
        transition: background-color 0.2s;
    }
    
    .export-btn:hover {
        background: #3367d6;
    }
    
    .export-btn:disabled {
        background: #ccc;
        cursor: not-allowed;
    }
    
    .export-progress {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
    }
    
    .success-message {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        color: #198754;
    }
    
    .modal-content {
        max-width: 800px;
        max-height: 80vh;
    }
    
    .modal-body {
        max-height: calc(80vh - 120px);
        overflow-y: auto;
    }
    
    .no-slides {
        text-align: center;
        color: #6c757d;
        padding: 40px;
    }
    
    .loading-overlay {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        gap: 1rem;
        color: #6b7280;
    }
    
    .loading-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #f3f4f6;
        border-top: 3px solid #3b82f6;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
</style> 